import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Link,
  Alert,
  Grid,
  Dialog,
  DialogContent,
  IconButton,
  Divider,
  Paper,
} from '@mui/material';
import {
  Architecture as ArchitectureIcon,
  SupportAgent as SupportIcon,
  Computer as ComputerIcon,
  Key as KeyIcon,
  School as SchoolIcon,
  SmartToy as AIIcon,
  MonitorHeart as MonitorIcon,
  Settings as SettingsIcon,
  EventNote as SessionsIcon,
  OpenInNew as OpenInNewIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
} from '@mui/icons-material';

const DARK_GREEN = '#1D4434';
const ORANGE = '#D74B00';

// ─── Data ────────────────────────────────────────────────────────────────────

const SEQUENCE = [
  { n: 1, label: 'Review Architecture', target: 'sec-architecture' },
  { n: 2, label: 'Support Access', target: 'sec-support' },
  { n: 3, label: 'Request VDI', target: 'sec-access' },
  { n: 4, label: 'GitHub Access', target: 'sec-github' },
  { n: 5, label: 'Set Up AI Tools', target: 'sec-ai' },
  { n: 6, label: 'Local Environment', target: 'sec-local' },
  { n: 7, label: 'Bookmark Monitoring', target: 'sec-monitoring' },
  { n: 8, label: 'Onboarding Tutorials', target: 'sec-learning' },
  { n: 9, label: 'Attend Sessions', target: 'sec-sessions' },
];

const NAV = [
  { id: 'sec-sessions',     label: 'Sessions',      emoji: '📅' },
  { id: 'sec-architecture', label: 'Architecture',  emoji: '🏗️' },
  { id: 'sec-support',      label: 'Support',       emoji: '🛟' },
  { id: 'sec-access',       label: 'Access & VDI',  emoji: '💻' },
  { id: 'sec-github',       label: 'GitHub',        emoji: '🔑' },
  { id: 'sec-learning',     label: 'Learning',      emoji: '📚' },
  { id: 'sec-ai',           label: 'AI Tools',      emoji: '🤖' },
  { id: 'sec-monitoring',   label: 'Monitoring',    emoji: '📊' },
  { id: 'sec-local',        label: 'Local Setup',   emoji: '⚙️' },
];

const SESSIONS = [
  {
    track: 'Track 1',
    tagColor: '#1d4ed8',
    tagBg: '#eff6ff',
    title: 'Functional Sessions',
    meta: [{ label: 'Owner', value: 'Sagar' }, { label: 'Duration', value: '45 mins' }],
    desc: 'Product and feature walkthroughs covering Collaborate and HighQ Hub capabilities.',
    topics: [
      { text: 'Overview of Collaborate, HighQ Hub' },
      { text: 'HighQ File Module' },
      { text: 'HighQ iSheet Module' },
      { text: 'HighQ Task & Social (Event / Blog / Wiki)' },
      { text: 'Admin — Site / System / ASP' },
      { text: 'HighQ Dashboards' },
      { text: 'HighQ AI Features' },
    ],
  },
  {
    track: 'Track 2',
    tagColor: '#7c3aed',
    tagBg: '#f5f3ff',
    title: 'Technical Sessions',
    meta: [{ label: 'Owner', value: 'Montu' }, { label: 'Duration', value: '45 mins' }],
    desc: 'Platform architecture and modernisation deep-dives for engineers.',
    topics: [
      { text: 'Collaborate Framework and Architecture' },
      { text: 'Modernised iSheet' },
      { text: 'General Microservice Architecture' },
    ],
  },
  {
    track: 'Track 3',
    tagColor: '#16a34a',
    tagBg: '#f0fdf4',
    title: 'Tools & Operational Sessions',
    meta: [{ label: 'Focus', value: 'Delivery, quality & tooling' }, { label: 'Duration', value: '30 mins' }],
    desc: 'How we branch, release, test, and work with ADO / GitHub — plus getting your local environment running.',
    topics: [
      { text: 'Branching Strategy', owner: 'Yogesh' },
      { text: 'Release & Deployment Management', owner: 'Sagar' },
      { text: 'Quality and Environment', owner: 'Sagar' },
      { text: 'ADO and GitHub Repo Sessions', owner: 'Sagar & Montu' },
      { text: 'Local Setup Session', owner: 'Parth' },
    ],
    span2: true,
  },
];

const ARCH_DIAGRAMS = [
  {
    tag: 'Legacy',
    tagColor: '#1d4ed8',
    tagBg: '#eff6ff',
    title: 'HighQ — Legacy High-Level Architecture',
    desc: 'Current-state view of the HighQ Collaborate platform before iSheet modernisation.',
    src: '/images/onboarding/arch-legacy.jpeg',
    caption: 'HighQ-High-Level.jpeg — click to enlarge',
  },
  {
    tag: 'iSheet Modernisation',
    tagColor: '#d97706',
    tagBg: '#fffbeb',
    title: 'iSheet Modernisation — High-Level Architecture',
    desc: 'Target-state view showing how iSheet is being extracted and modernised as part of this initiative.',
    src: '/images/onboarding/arch-isheet.jpeg',
    caption: 'iSheet Modernization.jpeg — click to enlarge',
  },
];

const RESOURCE_SECTIONS = [
  {
    id: 'sec-support',
    label: 'General Support',
    desc: 'Service Desk and live chat for day-to-day IT / tooling help',
    icon: <SupportIcon sx={{ fontSize: 20 }} />,
    color: '#1d4ed8',
    bgColor: '#eff6ff',
    items: [
      {
        tag: 'ServiceNow',
        tagColor: '#1d4ed8',
        tagBg: '#eff6ff',
        title: 'General Support Request',
        desc: 'Raise IT / tooling support tickets via the ServiceNow Employee Service Center catalog.',
        href: 'https://trenterprise.service-now.com/esc?id=sc_cat_item&sys_id=08d1f8c32f14f21025fbd44bcfa4e3ec&referrer=popular_items',
        btnLabel: 'Open General Support',
      },
      {
        tag: 'Microsoft Teams',
        tagColor: '#1d4ed8',
        tagBg: '#eff6ff',
        title: 'Chat with Service Desk',
        desc: 'Live chat with Service Desk Support for quicker answers on access, laptop, and account issues.',
        href: 'https://teams.microsoft.com/l/app/ce2c04bc-1b80-46f5-9900-3789c271cfd6?source=app-profile-card',
        btnLabel: 'Open Teams Chat',
      },
    ],
  },
  {
    id: 'sec-access',
    label: 'Access & VDI',
    desc: 'Virtual Desktop Infrastructure required for many internal systems',
    icon: <ComputerIcon sx={{ fontSize: 20 }} />,
    color: '#16a34a',
    bgColor: '#f0fdf4',
    items: [
      {
        tag: 'ServiceNow',
        tagColor: '#16a34a',
        tagBg: '#f0fdf4',
        title: 'VDI Request',
        desc: 'Request a Virtual Desktop (VDI) so you can reach restricted environments and corporate tools from your machine.',
        href: 'https://trenterprise.service-now.com/esc?id=sc_cat_item&sys_id=eb5595372b536690528efe4df291bf5c&table=sc_cat_item&searchTerm=vdi',
        btnLabel: 'Request VDI',
      },
    ],
  },
  {
    id: 'sec-github',
    label: 'GitHub Access & Commit Signing',
    desc: 'Repo access and digital certificate required for signed commits',
    icon: <KeyIcon sx={{ fontSize: 20 }} />,
    color: '#d97706',
    bgColor: '#fffbeb',
    items: [
      {
        tag: 'Wiki',
        tagColor: '#d97706',
        tagBg: '#fffbeb',
        title: 'GitHub Access Guide',
        desc: 'Follow the Collaborate wiki for how to request and configure GitHub access for HighQ repositories.',
        href: 'https://collaborate.highq.com/sitepoint/viewWikiPage.action?metaData.siteID=66&metaData.wikiID=57573',
        btnLabel: 'Open GitHub Access Wiki',
      },
      {
        tag: 'Certificate',
        tagColor: '#d97706',
        tagBg: '#fffbeb',
        title: 'Digital Certificate for GitHub Commits',
        desc: 'You need a digital certificate to sign GitHub commits. Raise a General Support request and route it to the GitHub support group.',
        steps: [
          'Open the General Support catalog item (link below).',
          'Set Request type to "Support request".',
          'Select assignment group: SERVICE-SUPPORT-GITHUB.',
          'Describe that you need a digital certificate for GitHub commit signing.',
        ],
        href: 'https://trenterprise.service-now.com/esc?id=sc_cat_item&sys_id=08d1f8c32f14f21025fbd44bcfa4e3ec&referrer=popular_items',
        btnLabel: 'Raise Support Request',
        secondaryHref: 'https://trenterprise.service-now.com/esc?id=sc_cat_item&sys_id=7b617f8dffffee902fc7fa2fec4fd942',
        secondaryLabel: 'Certificate catalog item',
        fullWidth: true,
      },
    ],
  },
  {
    id: 'sec-learning',
    label: 'Collaborate Video Tutorials & Learning',
    desc: 'Engineering onboarding material and training content',
    icon: <SchoolIcon sx={{ fontSize: 20 }} />,
    color: '#7c3aed',
    bgColor: '#f5f3ff',
    items: [
      {
        tag: 'Onboarding',
        tagColor: '#7c3aed',
        tagBg: '#f5f3ff',
        title: 'HighQ Engineering Onboarding',
        desc: 'Primary engineering onboarding wiki — product context, practices, and getting-started material for new engineers.',
        href: 'https://collaborate.highq.com/sitepoint/viewWikiPage.action?metaData.siteID=3904&metaData.wikiID=56848',
        btnLabel: 'Open Engineering Onboarding',
      },
      {
        tag: 'Training',
        tagColor: '#7c3aed',
        tagBg: '#f5f3ff',
        title: 'Training and Development',
        desc: 'Folder of training documents and video tutorials for Collaborate product and engineering skills.',
        href: 'https://collaborate.highq.com/sitepoint/documentHome.action?metaData.siteID=8373&metaData.parentFolderID=308424&metaData.moduleView=columnView&metaData.paginationNo=0&metaData.loadMoreNo=0&sortOrder=0&filterShared=0',
        btnLabel: 'Open Training Folder',
      },
    ],
  },
  {
    id: 'sec-ai',
    label: 'AI Tools & Licenses',
    desc: 'Claude, Cursor, and the TR AI platform for day-to-day development',
    icon: <AIIcon sx={{ fontSize: 20 }} />,
    color: '#0891b2',
    bgColor: '#ecfeff',
    callout: 'Request your Cursor license early — approval can take a business day or two. Claude setup guides are on SharePoint (VPN / corporate network usually required).',
    items: [
      {
        tag: 'Platform',
        tagColor: '#0891b2',
        tagBg: '#ecfeff',
        title: 'AI for General Purpose',
        desc: 'Thomson Reuters AI Experiences platform — general-purpose AI tools available to employees.',
        href: 'https://dataandanalytics.int.thomsonreuters.com/ai-platform/ai-experiences',
        btnLabel: 'Open AI Platform',
      },
      {
        tag: 'Claude',
        tagColor: '#0891b2',
        tagBg: '#ecfeff',
        title: 'Getting Started with Claude Code',
        desc: 'Distinguished Engineer SharePoint page covering Claude Code setup for TR engineers.',
        href: 'https://trten.sharepoint.com/sites/intr-distinguished-engineer/SitePages/Getting-Started-with-Claude-Code.aspx',
        btnLabel: 'Claude Code Getting Started',
      },
      {
        tag: 'Claude',
        tagColor: '#0891b2',
        tagBg: '#ecfeff',
        title: 'Claude.ai & Claude Code — Setup Guide',
        desc: 'Full setup guide for Claude.ai and Claude Code from the AI-Powered Developer Tools site.',
        href: 'https://trten.sharepoint.com/sites/intr-ai-powered-developer-tools/SitePages/Claude.ai-&-Claude-Code-%E2%80%94-Setup-Guide.aspx',
        btnLabel: 'Open Claude Setup Guide',
      },
      {
        tag: 'Cursor',
        tagColor: '#0891b2',
        tagBg: '#ecfeff',
        title: 'Receive Cursor License',
        desc: 'Request / claim your Cursor IDE license via the Preci AI Services portal.',
        href: 'https://preci.thomsonreuters.com/ai-services/cursor-license-grant',
        btnLabel: 'Request Cursor License',
      },
      {
        tag: 'Cursor',
        tagColor: '#0891b2',
        tagBg: '#ecfeff',
        title: 'Cursor PR Setup',
        desc: 'Team guidance on configuring Cursor for PR workflows — posted in the Microservice Modernisation and Migration Teams channel.',
        href: 'https://teams.microsoft.com/l/message/19:At3ahOq-HMr1ygb-9-MwSJKKSHto9g4ixXyaybbNlM81@thread.tacv2/1773300664667?tenantId=62ccb864-6a1a-4b5d-8e1c-397dec1a8258&groupId=51445983-5c49-4603-8ed4-d28dfeb7d45f&parentMessageId=1773300664667&teamName=Microservice%20Modernization%20and%20Migration&channelName=All%20team%20member&createdTime=1773300664667',
        btnLabel: 'Open Teams Message',
      },
    ],
  },
  {
    id: 'sec-monitoring',
    label: 'Monitoring',
    desc: 'Production observability for HighQ services',
    icon: <MonitorIcon sx={{ fontSize: 20 }} />,
    color: '#dc2626',
    bgColor: '#fef2f2',
    items: [
      {
        tag: 'Datadog',
        tagColor: '#dc2626',
        tagBg: '#fef2f2',
        title: 'Datadog — HighQ Production',
        desc: 'Primary monitoring and APM platform for HighQ production. Bookmark this and request access if you cannot log in.',
        href: 'https://tr-highq-prod.datadoghq.com/',
        btnLabel: 'Open Datadog',
      },
    ],
  },
  {
    id: 'sec-local',
    label: 'Local Instance Setup',
    desc: 'Run Collaborate on your local machine for development',
    icon: <SettingsIcon sx={{ fontSize: 20 }} />,
    color: '#475569',
    bgColor: '#f1f5f9',
    callout: 'The local setup document is shared via Collaborate external access. You may need an active Collaborate session or the shared link password from your buddy / manager.',
    calloutSeverity: 'warning',
    items: [
      {
        tag: 'Documentation',
        tagColor: '#475569',
        tagBg: '#f1f5f9',
        title: 'Set Up a Local Instance',
        desc: 'Step-by-step guide to install and run a Collaborate instance on your local development machine.',
        href: 'https://collaborate.highq.com/sitepoint/externalAccess.action?linkParam=~hqWwnHINR0mQCMcgWPW_FhrD8xFSlSVMka9ERZteCpa2WYYHLOiuLWpDCBAcMe5VbAS8oD-8c0AGiCNo8&documentDownload=link',
        btnLabel: 'Open Local Setup Guide',
      },
    ],
  },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

const Tag = ({ label, color, bg }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      fontSize: 10.5,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.7px',
      px: 1.25,
      py: '3px',
      borderRadius: '20px',
      bgcolor: bg,
      color,
      mb: 1,
    }}
  >
    {label}
  </Box>
);

const ActionBtn = ({ href, label, secondary = false }) => (
  <Link
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    underline="none"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      px: 1.75,
      py: '7px',
      borderRadius: '8px',
      fontSize: 13,
      fontWeight: 600,
      transition: 'all .15s',
      ...(secondary
        ? {
            border: `1px solid #cbd5e1`,
            color: '#475569',
            bgcolor: '#fff',
            '&:hover': { bgcolor: '#f1f5f9', borderColor: '#94a3b8' },
          }
        : {
            bgcolor: DARK_GREEN,
            color: '#fff',
            '&:hover': { bgcolor: ORANGE },
          }),
    }}
  >
    {label}
    <OpenInNewIcon sx={{ fontSize: 13 }} />
  </Link>
);

const SectionHeader = ({ id, icon, iconColor, iconBg, title, desc }) => (
  <Box id={id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2.5, scrollMarginTop: '56px' }}>
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: iconBg,
        color: iconColor,
        flexShrink: 0,
        mt: 0.25,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography fontWeight={700} fontSize={18} sx={{ color: DARK_GREEN, lineHeight: 1.3 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" mt={0.25}>
        {desc}
      </Typography>
    </Box>
  </Box>
);

const ResourceCard = ({ item }) => (
  <Card
    variant="outlined"
    sx={{
      borderRadius: '12px',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      transition: 'box-shadow .15s, border-color .15s',
      '&:hover': { boxShadow: '0 4px 14px rgba(15,23,42,.08)', borderColor: '#cbd5e1' },
    }}
  >
    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Tag label={item.tag} color={item.tagColor} bg={item.tagBg} />
      <Typography fontWeight={700} fontSize={14.5} mb={0.75}>
        {item.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ flex: 1, mb: 1.5 }}>
        {item.desc}
      </Typography>

      {item.steps && (
        <Box
          sx={{
            bgcolor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            p: 1.5,
            mb: 1.5,
          }}
        >
          <Typography fontSize={12} fontWeight={700} mb={0.75} color="text.primary">
            How to raise the request
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
            {item.steps.map((s, i) => (
              <Box
                component="li"
                key={i}
                sx={{ fontSize: 12.5, color: '#475569', mb: 0.5, lineHeight: 1.5 }}
              >
                {s}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 'auto' }}>
        {item.href && <ActionBtn href={item.href} label={item.btnLabel} />}
        {item.secondaryHref && (
          <ActionBtn href={item.secondaryHref} label={item.secondaryLabel} secondary />
        )}
        {!item.href && (
          <Typography variant="caption" color="text.secondary" fontStyle="italic">
            Ask your team lead or buddy for access.
          </Typography>
        )}
      </Box>
    </CardContent>
  </Card>
);

// ─── Main component ───────────────────────────────────────────────────────────

const OnboardingTab = () => {
  const [lightbox, setLightbox] = useState(null);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box>
      {/* ── Sticky section nav ─────────────────────────────────── */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          bgcolor: 'rgba(255,255,255,0.96)',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid #e2e8f0',
          px: 3,
          py: 1,
          mx: -3,
          display: 'flex',
          gap: 0.5,
          overflowX: 'auto',
          '&::-webkit-scrollbar': { height: 0 },
        }}
      >
        {NAV.map((n) => (
          <Box
            key={n.id}
            component="button"
            onClick={() => scrollTo(n.id)}
            sx={{
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.75,
              border: '1px solid #e2e8f0',
              borderRadius: '20px',
              bgcolor: '#f8fafc',
              color: '#475569',
              fontSize: 12.5,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all .15s',
              '&:hover': { bgcolor: DARK_GREEN, color: '#fff', borderColor: DARK_GREEN },
            }}
          >
            <span style={{ fontSize: 14 }}>{n.emoji}</span>
            {n.label}
          </Box>
        ))}
      </Box>

      <Box sx={{ p: 3 }}>
        {/* ── Recommended sequence ──────────────────────────────── */}
        <Paper
          elevation={0}
          sx={{
            border: '1px solid #e2e8f0',
            borderRadius: '14px',
            p: 3,
            mb: 4,
            bgcolor: '#f8fafc',
          }}
        >
          <Typography fontWeight={700} fontSize={16} mb={0.5} sx={{ color: DARK_GREEN }}>
            Recommended Onboarding Sequence
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2.5}>
            Work through these in order — each step unlocks the next. Click a step to jump to details.
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))',
              gap: 1.25,
            }}
          >
            {SEQUENCE.map((s) => (
              <Box
                key={s.n}
                component="button"
                onClick={() => scrollTo(s.target)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  p: '10px 14px',
                  bgcolor: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#1e293b',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'border-color .15s, box-shadow .15s',
                  '&:hover': {
                    borderColor: '#94a3b8',
                    boxShadow: '0 2px 8px rgba(15,23,42,.08)',
                  },
                }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    bgcolor: ORANGE,
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {s.n}
                </Box>
                {s.label}
              </Box>
            ))}
          </Box>
        </Paper>

        {/* ── Sessions Plan ─────────────────────────────────────── */}
        <Box id="sec-sessions" sx={{ mb: 5, scrollMarginTop: '56px' }}>
          <SectionHeader
            id="sec-sessions-header"
            icon={<SessionsIcon sx={{ fontSize: 20 }} />}
            iconColor="#d97706"
            iconBg="#fffbeb"
            title="Onboarding Sessions Plan"
            desc="Structured functional, technical, and operational sessions for new joiners"
          />
          <Alert severity="info" sx={{ mb: 2.5, fontSize: 13 }}>
            <strong>How onboarding sessions work:</strong> Sessions are split into three tracks — Functional
            (product walkthroughs), Technical (architecture & platform), and Tools & Operational (delivery
            practices). Coordinate timing with your buddy / manager.
          </Alert>
          <Grid container spacing={2}>
            {SESSIONS.map((s) => (
              <Grid item xs={12} md={s.span2 ? 12 : 6} key={s.track}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: '12px',
                    height: '100%',
                    transition: 'box-shadow .15s, border-color .15s',
                    '&:hover': { boxShadow: '0 4px 14px rgba(15,23,42,.08)', borderColor: '#cbd5e1' },
                  }}
                >
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Tag label={s.track} color={s.tagColor} bg={s.tagBg} />
                    <Typography fontWeight={700} fontSize={15} mb={1}>
                      {s.title}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                      {s.meta.map((m) => (
                        <Box
                          key={m.label}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1.25,
                            py: 0.5,
                            bgcolor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: '20px',
                            fontSize: 12,
                          }}
                        >
                          <Typography component="span" fontSize={12} color="text.secondary">
                            {m.label}
                          </Typography>
                          <Typography component="span" fontSize={12} fontWeight={600}>
                            {m.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    <Typography variant="body2" color="text.secondary" mb={1.5}>
                      {s.desc}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {s.topics.map((t, i) => (
                        <Box
                          key={i}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            px: 1.5,
                            py: '8px',
                            bgcolor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: 13,
                          }}
                        >
                          <Box
                            sx={{
                              flexShrink: 0,
                              width: 7,
                              height: 7,
                              borderRadius: '50%',
                              bgcolor: ORANGE,
                            }}
                          />
                          <Typography fontSize={13} sx={{ flex: 1 }}>
                            {t.text}
                          </Typography>
                          {t.owner && (
                            <Box
                              sx={{
                                flexShrink: 0,
                                fontSize: 11.5,
                                fontWeight: 600,
                                color: '#64748b',
                                bgcolor: '#fff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '12px',
                                px: 1,
                                py: '2px',
                              }}
                            >
                              {t.owner}
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* ── Architecture Overview ─────────────────────────────── */}
        <Box id="sec-architecture" sx={{ mb: 5, scrollMarginTop: '56px' }}>
          <SectionHeader
            id="sec-architecture-header"
            icon={<ArchitectureIcon sx={{ fontSize: 20 }} />}
            iconColor="#7c3aed"
            iconBg="#f5f3ff"
            title="Architecture Overview"
            desc="High-level system architecture — legacy platform and iSheet Modernisation"
          />
          <Alert severity="info" sx={{ mb: 2.5, fontSize: 13 }}>
            <strong>Context:</strong> Use these two diagrams to orient yourself before diving into the
            codebase — the legacy diagram shows the current monolithic HighQ platform, and the
            modernisation diagram shows where iSheet is heading (microservice extraction, decoupled data
            flow). Ask your team lead or buddy to walk through these in your first week.
          </Alert>
          <Grid container spacing={2}>
            {ARCH_DIAGRAMS.map((d) => (
              <Grid item xs={12} md={6} key={d.title}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: '12px',
                    transition: 'box-shadow .15s, border-color .15s',
                    '&:hover': { boxShadow: '0 4px 14px rgba(15,23,42,.08)', borderColor: '#cbd5e1' },
                  }}
                >
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Tag label={d.tag} color={d.tagColor} bg={d.tagBg} />
                    <Typography fontWeight={700} fontSize={14.5} mb={0.5}>
                      {d.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={1.5}>
                      {d.desc}
                    </Typography>
                    <Box
                      sx={{ position: 'relative', cursor: 'zoom-in' }}
                      onClick={() => setLightbox(d.src)}
                    >
                      <Box
                        component="img"
                        src={d.src}
                        alt={d.title}
                        sx={{
                          width: '100%',
                          display: 'block',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                          bgcolor: '#fff',
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'rgba(15,23,42,.55)',
                          color: '#fff',
                          borderRadius: '6px',
                          p: '4px 8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontSize: 11.5,
                          fontWeight: 600,
                          pointerEvents: 'none',
                        }}
                      >
                        <ZoomInIcon sx={{ fontSize: 14 }} />
                        Click to enlarge
                      </Box>
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      textAlign="center"
                      mt={1}
                    >
                      {d.caption}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* ── Resource sections ─────────────────────────────────── */}
        {RESOURCE_SECTIONS.map((section, si) => (
          <Box key={section.id} id={section.id} sx={{ mb: 5, scrollMarginTop: '56px' }}>
            <SectionHeader
              icon={section.icon}
              iconColor={section.color}
              iconBg={section.bgColor}
              title={section.label}
              desc={section.desc}
            />
            {section.callout && (
              <Alert
                severity={section.calloutSeverity || 'info'}
                sx={{ mb: 2.5, fontSize: 13 }}
              >
                {section.callout}
              </Alert>
            )}
            <Grid container spacing={2}>
              {section.items.map((item) => (
                <Grid item xs={12} md={item.fullWidth ? 12 : 6} key={item.title}>
                  <ResourceCard item={item} />
                </Grid>
              ))}
            </Grid>
            {si < RESOURCE_SECTIONS.length - 1 && <Divider sx={{ mt: 4 }} />}
          </Box>
        ))}
      </Box>

      {/* ── Lightbox ──────────────────────────────────────────────── */}
      <Dialog
        open={!!lightbox}
        onClose={() => setLightbox(null)}
        maxWidth={false}
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            maxWidth: '92vw',
            maxHeight: '92vh',
          },
        }}
        BackdropProps={{ sx: { bgcolor: 'rgba(15,23,42,.88)' } }}
      >
        <DialogContent sx={{ p: 0, position: 'relative', overflow: 'hidden', display: 'flex' }}>
          <IconButton
            onClick={() => setLightbox(null)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(15,23,42,.65)',
              color: '#fff',
              zIndex: 1,
              '&:hover': { bgcolor: 'rgba(15,23,42,.85)' },
            }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          {lightbox && (
            <Box
              component="img"
              src={lightbox}
              alt="Architecture diagram enlarged"
              sx={{
                maxWidth: '92vw',
                maxHeight: '92vh',
                borderRadius: '8px',
                display: 'block',
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default OnboardingTab;
